import { z } from 'zod';
import { chatMessageInputSchema, chatMessageSchema } from './chat.js';

/**
 * What travels on the chat's two transports (chat spec §7, §8).
 *
 * A file of its own rather than more of `chat.ts`, and the cut is by SUBJECT:
 * `chat.ts` holds the shapes of the room and its history - what a screen reads
 * and what the store writes - while this one holds the frames and the one bound
 * that exist only because the conversation is carried over a socket and a POST.
 * A reader looking for « what does the wire look like » finds it whole here,
 * and the two files can move for different reasons.
 */

/**
 * The transport's safety bound on one message, in CHARACTERS.
 *
 * **It is not a product ceiling** - §14 decided there is none, and §8's queue
 * is what makes that safe. Nothing in an interview limits how long an answer
 * may be, and the composer says nothing about this number: no counter, no
 * `maxLength`, nothing an écrivain could read as « make it shorter ». Somebody
 * pasting three pages of notes is legitimate material and must go through.
 *
 * What it stops is the OTHER thing: a paste of a whole file, which would put a
 * multi-megabyte frame on a socket every participant of the room has to
 * receive, and a body nginx refuses at its own default of 1 MB - a refusal the
 * client-side queue would then retry for ever. 20 000 characters is about three
 * thousand words: an order of magnitude past any interview turn ever written,
 * and two orders under where either of those two things starts to hurt.
 *
 * Shared rather than backend-only because the queue has to be able to tell a
 * PERMANENT refusal from a temporary one. A message over this bound can never
 * be accepted, so the client drops it from the queue and says so, instead of
 * re-posting it at every reconnection until the person closes the tab.
 */
export const CHAT_MESSAGE_MAX_CHARS = 20_000;

/**
 * A posted message, with the transport's bound applied.
 *
 * Derived from `chatMessageInputSchema` rather than restated, so the fields and
 * the uuid rule stay declared in one place: the bound is the only thing this
 * layer adds, and a second copy of the shape would drift from the first.
 */
export const chatBoundedMessageInputSchema = chatMessageInputSchema.extend({
  text: z.string().min(1).max(CHAT_MESSAGE_MAX_CHARS),
});
export type ChatBoundedMessageInput = z.infer<typeof chatBoundedMessageInputSchema>;

/**
 * What the POST hands back.
 *
 * `duplicate` is on the wire because the caller is a QUEUE, and a queue has to
 * be able to tell « accepted » from « you already sent this one » without
 * guessing from a status code. Both are a success and both carry the stored
 * message - the client needs the `seq` it never received (§8.1) - so the
 * difference could not have been carried by the status.
 */
export const chatMessageAcceptedSchema = z
  .object({ message: chatMessageSchema, duplicate: z.boolean() })
  .strict();
export type ChatMessageAccepted = z.infer<typeof chatMessageAcceptedSchema>;

/**
 * A frame the SERVER sends down a chat socket.
 *
 * Two of them, and neither carries anything the REST read does not: the socket
 * is the DELTA channel (§7) and the history stays the source of truth. A client
 * that missed a frame re-reads and loses nothing, which is the property that
 * makes a reconnection cheap to write.
 *
 * `ping` is the server-driven heartbeat of §8.3, already spelled as
 * `CHAT_PING_FRAME` in `chat/socket.ts` - the literal here is the same string,
 * and the schema is what makes the client parse it rather than match on it.
 *
 * A discriminated union and `.strict()` on both members, so a frame the client
 * does not understand fails the parse loudly instead of being read as a message
 * with no text. An LLM output is not the only external input worth parsing: a
 * socket frame is one too.
 */
export const chatServerFrameSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('ping') }).strict(),
  z.object({ type: z.literal('message'), message: chatMessageSchema }).strict(),
]);
export type ChatServerFrame = z.infer<typeof chatServerFrameSchema>;

/**
 * The client's answer to a ping, by convention.
 *
 * The registry does not care what it says - ANY inbound frame proves the peer
 * alive - but a constant beats a literal typed in a component: the two ends of
 * a convention have to be readable from one place, or the day one of them
 * changes only a comment says the other should have.
 */
export const CHAT_PONG_FRAME = '{"type":"pong"}';
